import React, { forwardRef } from 'react';

import type { TextInput as RNTextInput } from 'react-native';

import { TextInput } from '../inputs';
import type { TextInputProps } from '../inputs';

export interface AccountPasswordInputProps extends Omit<
  TextInputProps,
  'textContentType' | 'ariaLabelledBy' | 'ariaLabel'
> {
  /**
   * Name of the account form in analytics events
   */
  analyticsName: string;
  /**
   * Support text to display below the input (e.g., password requirements)
   * @default "5-128 characters"
   */
  supportText?: string;
  /**
   * Custom validation function for password
   * @default Validates length between 5-128 characters
   */
  customValidator?: (password: string) => boolean;
  /**
   * Error message to display on validation failure
   */
  validationErrorText?: string;
  /**
   * ARIA labelledBy attribute
   */
  ariaLabelledBy?: string;
}

/**
 * AccountPasswordInput is a specialized password input component for account authentication flows.
 * It extends TextInput with password-specific configuration and validation.
 *
 * Features:
 * - Password obfuscation (type="password")
 * - Configurable password validation (default: 5-128 characters)
 * - Support text for password requirements
 * - Consistent styling across account forms
 *
 * @example
 * ```tsx
 * <AccountPasswordInput
 *   analyticsName="my-app-login"
 *   title="Password"
 *   onChangeText={setPassword}
 *   supportText="5-128 characters"
 * />
 * ```
 */
export const AccountPasswordInput = forwardRef<RNTextInput, AccountPasswordInputProps>(
  (
    {
      analyticsName,
      supportText = '5-128 characters',
      customValidator,
      validationErrorText,
      title = 'Password',
      size = 'medium',
      required = true,
      ariaLabelledBy,
      ...props
    },
    ref,
  ) => {
    return (
      <TextInput
        ref={ref}
        id="password"
        analyticsName={`${analyticsName}-password`}
        size={size}
        required={required}
        textContentType="password"
        supportText={supportText}
        {...(ariaLabelledBy ? { ariaLabelledBy } : { title })}
        {...props}
      />
    );
  },
);

AccountPasswordInput.displayName = 'AccountPasswordInput';
